import { ComponentFactoryResolver, Injectable, Injector } from '@angular/core';

import { CookieService } from 'ngx-cookie-service';

import { PiwikScriptComponent } from './piwik-script.component';
import { IPiwikConfig, LibConfig } from './lib.config';

/**
 * Ausnahmsweise nutzen wir hier NICHT "@Injectable({providedIn: 'root'})".
 *
 * Stattdessen wird die Instanz des Services als Provider im piwikModule registriert,
 * da wir dort mit dem "APP_INITIALIZER" die Metehode "PiwikService.init()"
 * beim Bootstrapen der App ausführen können.
 *
 * siehe: https://angular.io/guide/architecture-services
 */

@Injectable()
export class PiwikService {
    private _config: IPiwikConfig = this.libConfig.getPiwikConfig();

    constructor(
        private _cookieService: CookieService,
        private _componentFactoryResolver: ComponentFactoryResolver,
        private _injector: Injector,
        private libConfig: LibConfig
    ) {}

    /**
     * Injekted das PIWIK Script ans Ende des <body>, wenn
     * - "cookie_consent" nicht "denied" ist (Datenschutz Cookie)
     * - "trackingActivated" = true ist  (environment.ts)
     */
    init(): void {
        if (!this._isTrackingAllowed()) {
            return;
        }
        this._configurePiwik();
        this._appendPiwikScriptComponent();
    }

    /**
     * Page views werden getracked
     * @param title
     */
    trackPageVisit(title: string) {
        if (this._isTrackingAllowed()) {
            // -- Hashrouting wird wohl im BA PIWIK nicht angebildet :(
            (window as any)._paq.push(['setCustomUrl', window.location.href]);
            (window as any)._paq.push(['setDocumentTitle', title]);
            (window as any)._paq.push(['trackPageView']);
        }
    }

    /**
     *
     * trackEvent-Tracking. Tracking von Clicks etc.
     *
     * @param action
     * @param category
     * @param name
     * @param value
     */
    trackEvent(category: string, action: string, name = '', value = 0) {
        if (this._isTrackingAllowed()) {
            (window as any)._paq.push(['trackEvent', category, action, name, value]);
        }
    }

    /**
     * Link-Tracking aktivieren
     *
     * @param url
     */
    trackOutlink(url: string) {
        if (this._isTrackingAllowed()) {
            (window as any)._paq.push(['trackLink', url, 'link']);
        }
    }

    /**
     * Konfiguriert PIWIK
     * siehe: https://developer.matomo.org/api-reference/tracking-javascript
     */
    _configurePiwik(): void {
        const userJourneyClientID = 1000;
        const piwikTrackerObject = (window as any)._paq || [];
        piwikTrackerObject.push(['setTrackerUrl', this._config.trackUrl]);
        piwikTrackerObject.push(['setSiteId', this._config.siteId]);
        // -- Set second tracking sideId -> https://matomo.org/faq/how-to/how-do-i-send-tracking-requests-to-two-or-more-matomo-servers/
        piwikTrackerObject.push(['addTracker', this._config.trackUrl, userJourneyClientID]);
        // -- Set delay for link tracking in milliseconds.
        piwikTrackerObject.push(['setLinkTrackingTimer', 500]);
        // -- Logs a visit to this page
        piwikTrackerObject.push(['trackPageView']);
        // -- Set to true to not track users who opt out of tracking using Mozilla's (proposed) Do Not Track setting.
        piwikTrackerObject.push(['setDoNotTrack', true]);
        // -- send requests to Matomo in order to better measure the time spent on the page
        piwikTrackerObject.push(['enableHeartBeatTimer']);
        // -- Install link tracking on all applicable link elements
        piwikTrackerObject.push(['enableLinkTracking']);

        (window as any)._paq = piwikTrackerObject;
    }

    /**
     * Return true, wenn tracking aktiv und Cookie "cookie_consent" NICHT den Wert "denied" hat.
     * - Prüft ob Tracking aktiviert ist (siehe environment.ts)
     * - Prüft "cookie_consent" !== "denied" (das Datenschutz Disclaimer Cookie)
     */
    _isTrackingAllowed(): boolean {
        if (!this._config.trackingActivated) {
            return false;
        }
        return this._isTrackingAllowed_byCookie();
    }

    /**
     * Prüft ob das Tracking evtl. durch das Cookie "cookie_consent" NICHT erlaubt ist.
     *
     * Wann ist es nicht erlaubt (Stand 02.2019):
     * - wenn das Cookie "cookie_consent" den Wert "denied" hat
     *
     * Hinweis:
     * Momentan ist das BA-Tracking nach dem Verfahren "OPT OUT" umgesetzt.
     * Das bedeutet, dass der Benutzer
     * auf der Datenschutz-Seite aktiv widersprechen muss (setzt cookie_consent auf "denied"), ...
     * ... um das Tracking zu unterbinden!
     */
    _isTrackingAllowed_byCookie(): boolean {
        const cookieValue: string = this._cookieService.get(this._config.disclaimerCookie.name);
        // -- Nicht erlaubt, wenn Cookie den Wert "denied" hat
        if (cookieValue !== this._config.disclaimerCookie.denied) {
            return true;
        }
        return false;
    }

    /**
     * Instanziiert dynamisch die Piwik-Script Component.
     * Die Piwik-Script Componente injektet das PIWIK Script ans Ende des Body Tags
     */
    _appendPiwikScriptComponent(): void {
        const componentFactory = this._componentFactoryResolver.resolveComponentFactory(PiwikScriptComponent);
        componentFactory.create(this._injector);
    }
}
